/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import _ from 'lodash';
import can from 'can';
import DomEventsManager from 'scripts/dom-events-manager';
import dtm from 'scripts/services/dtm';
import drawerView from './leaderboard-drawer.stache';
import appContext from 'scripts/app-context';
import { updateComparator } from '../utils';

import YearFallbackFeedFacade from '../year-fallback-feed-facade';

import template from './leaderboard.stache';
import dayjs from 'dayjs';

const scoringLeaderboardFeed =
    window.globalSettings.feeds.history.scoring.scoring;
const subscriptionKey = window.globalSettings.feeds.history.subscriptionKey;

Component.extend({
    tag: 'history-experience-scoring-leaderboard',
    template: template,
    leakScope: false,
    lazyLoadingDisabled: true,
    activeDrawer: null,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            fetchingFailed: {
                type: 'boolean',
                value: false,
            },
            players: {
                Type: Map.List,
                Value: Map.List,
            },
            legend: {
                type: 'object',
                value: '',
            },
            currentYear: {
                type: 'number',
                value: 2018,
            },
            activeTab: {
                Type: can.Map,
                Value: can.Map,
            },
            sortKey: {
                type: 'string',
                value: '',
            },
            sortAsc: {
                type: 'boolean',
                value: true,
            },
        },
        createSortingStatField(round) {
            return `roundScores.${round}`;
        },

        getLegend(currentYear) {
            const legendArr = this.attr('legend') || [];
            const legend = _.find(
                legendArr,
                (it) => it.year === parseInt(this.attr('yearSelected')),
            );
            let result = [];

            if (_.get(legend, 'location')) {
                result.push(legend.location);
            }

            if (_.get(legend, 'start')) {
                let dateResult = dayjs(legend.start).format('MMMM DD');

                if (_.get(legend, 'end')) {
                    const endMomentDate = dayjs(legend.end);
                    dateResult +=
                        ' - ' +
                        endMomentDate.format(
                            endMomentDate.isSame(dayjs(legend.start), 'month')
                                ? 'DD'
                                : 'MMMM DD',
                        );
                }

                result.push(dateResult);
            }

            const years = this.attr('settings.legendYears');
            if (
                years &&
                currentYear &&
                years.indexOf(currentYear.toString()) != -1
            ) {
                result.push(this.attr('settings.legendYearText'));
            }

            return result.join(', ');
        },
        getSortingField(sortKey) {
            switch (sortKey) {
                case 'pos':
                    return 'pos';
                case 'player':
                    return 'lastName';
                case 'to-par':
                    return 'toPar';
                case '1':
                    return this.createSortingStatField('0');
                case '2':
                    return this.createSortingStatField('1');
                case '3':
                    return this.createSortingStatField('2');
                case '4':
                    return this.createSortingStatField('3');
                default:
                    return 'pos';
            }
        },
    },
    events: {
        inserted() {
            this.registerFeedService();
            this.fetchData();

            appContext.model.bind(
                'history-experience-scoring-and-stats-years-change',
                this.handleYearChange.bind(this),
            );
            appContext.model.bind(
                'history-experience-scoring-and-stats-years-ready',
                (_, years, legend) => {
                    this.viewModel.attr('legend', legend);
                },
            );

            DomEventsManager.subscribe(this);

            this.viewModel.bind('sortKey', () => {
                updateComparator(this.viewModel, 'players');
            });
            this.viewModel.bind('sortAsc', () => {
                updateComparator(this.viewModel, 'players');
            });
        },

        removed() {
            DomEventsManager.unsubscribe(this);
        },

        registerFeedService() {
            const headers = subscriptionKey
                ? { ['Ocp-Apim-Subscription-Key']: subscriptionKey }
                : {};

            this.feedService = new YearFallbackFeedFacade({
                feedUrl: scoringLeaderboardFeed,
                fromCache: false,
                autoUpdate: false,
                headers,
            })
                .onData((__, data) => {
                    this.viewModel.attr('fetching', false);

                    let players = _.get(data, 'players', []);
                    players.forEach((player) => {
                        player.roundPars = [];
                        player.rounds.forEach((round) => {
                            let roundPar = 0;
                            if (round.holes) {
                                round.holes.forEach((hole) => {
                                    roundPar += hole.par;
                                });
                            }
                            player.roundPars.push(roundPar);
                        });
                    });

                    can.batch.start();
                    this.viewModel.attr('players', players);
                    updateComparator(this.viewModel, 'players');
                    can.batch.stop();
                })
                .onError((_, error) => {
                    console.warn(`Error - ${error}`);
                    this.viewModel.attr('fetching', false);
                    this.viewModel.attr('fetchingFailed', true);
                });
        },

        fetchData(identifier = 'meta') {
            this.viewModel.attr('fetchingFailed', false);
            this.viewModel.attr('fetching', true);
            this.feedService.fetch(identifier, { identifier: identifier });
        },

        toggleDrawer($el) {
            let identifier = $el.data('identifier');
            let playerName = $el.data('name');
            let activeDrawerIdentifier = null;

            if (this.activeDrawer) {
                activeDrawerIdentifier = this.activeDrawer.attr(
                    'data-identifier',
                );
                this.removeDrawer();
                $el.find('.js-cell-player-button').attr('aria-expanded', false);
            } else {
                dtm.trackLeaderboardExpand(playerName);
            }
            if (activeDrawerIdentifier != identifier) {
                $el.parent()
                    .find('.js-cell-player-button')
                    .attr('aria-expanded', false);
                $el.find('.js-cell-player-button').attr('aria-expanded', true);
                let data = this.viewModel.players.filter(
                    (item) => item.id == identifier,
                )[0];
                if (data) {
                    $el.after(
                        drawerView({
                            data: data,
                            removeDrawer: this.removeDrawer.bind(this),
                        }),
                    );
                    this.activeDrawer = $el.next();
                }
            }
        },

        removeDrawer() {
            if (this.activeDrawer) {
                this.activeDrawer.remove();
                this.activeDrawer = null;
            }
        },

        handleYearChange(_, year) {
            if (!Boolean(year)) {
                return;
            }

            this.removeDrawer();
            this.viewModel.attr('yearSelected', year);
            this.fetchData(year);
        },

        '.js-board-row click'(element, event) {
            this.toggleDrawer(element);
        },

        '.js-sorting-field click'(element, event) {
            const sortKey = element.attr('data-sort-key');
            can.batch.start();
            if (this.viewModel.attr('sortKey') === sortKey) {
                this.viewModel.attr('sortAsc', !this.viewModel.attr('sortAsc'));
            } else {
                this.viewModel.attr('sortKey', sortKey);
                this.viewModel.attr('sortAsc', true);
            }

            can.batch.stop();
            dtm.trackRoundTrackerClick(sortKey);
        },
    },
});
